import _ from 'lodash';
import 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import ErrorBoundary from 'react-error-boundary';
import {
  Dimmer,
  Grid,
  Loader,
  Menu,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { platformServices } from '../../Common/Values';
import { fetchMembersWithOptions } from '../../../services/MembersApi';
import { useGeneralParams } from '../../GeneralParams';
import { useStitchAuth } from '../../StitchAuth';
import { useStitchMembers } from '../../StitchMembers';
import GroupActions from './GroupActions';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import {
  buildMemberListQuery,
  SERVICE_TABS,
} from './memberListQuery';

const formatTabLabel = (label, count) =>
  count === null ? label : `${label} (${count})`;

const buildColumns = (rows) => ({
  bodyWeight: rows.some((x) => x.bodyWeight),
  systolicBP: rows.some((x) => x.systolicBP),
  pulse: rows.some((x) => x.pulse),
  ecg: rows.some((x) => x.ecg),
  bloodSugar: rows.some((x) => x.bloodSugar),
  step: rows.some((x) => x.step),
  spo2: rows.some((x) => x.spo2),
  pef: rows.some((x) => x.pef),
});

const EMPTY_COLUMNS = {
  bodyWeight: false,
  systolicBP: false,
  pulse: false,
  ecg: false,
  bloodSugar: false,
  step: false,
  spo2: false,
  pef: false,
};

export default function ContentTable({ listFilter }) {
  const { userCustomData } = useStitchAuth();
  const { memberAll, nTot, nLimit, actions } = useStitchMembers();
  const { showParam, sortParam, setSortParam, pageNum, setPageNum } =
    useGeneralParams();
  const [members, setMembers] = useState([]);
  const [columns, setColumns] = useState(EMPTY_COLUMNS);
  const [loading, setLoading] = useState(false);
  const [membersSelected, setMembersSelected] = useState(new Set());
  const [memberServiceTab, setMemberServiceTab] = useState('all');
  const [tabCounts, setTabCounts] = useState({
    all: null,
    rpm: null,
    ccm: null,
    rpmCcm: null,
  });

  const showServiceTabs =
    (listFilter === 'all-members' || listFilter === 'disenrolled') &&
    (userCustomData?.services || []).includes(platformServices.CCM);

  const sortOption = useMemo(() => {
    const sortMap = {
      measureDateAsc: { dataReceivedDT: 1 },
      activityDesc: { measureFreq: -1 },
      activityAsc: { measureFreq: 1 },
      nameDesc: { name: -1 },
      nameAsc: { name: 1 },
    };
    return sortMap[sortParam] || { dataReceivedDT: -1 };
  }, [sortParam]);

  const handleServiceTabChange = (tab) => {
    setMemberServiceTab(tab);
    setPageNum(0);
  };

  useEffect(() => {
    setMemberServiceTab('all');
  }, [listFilter]);

  useEffect(() => {
    setMembersSelected(new Set());
  }, [listFilter, showParam, memberServiceTab]);

  useEffect(() => {
    if (!userCustomData || !showServiceTabs) {
      return;
    }

    let cancelled = false;
    setTabCounts({ all: null, rpm: null, ccm: null, rpmCcm: null });

    const fetchCount = (tab) =>
      fetchMembersWithOptions({
        query: buildMemberListQuery(listFilter, showParam, tab, true),
        projection: { _id: 1 },
        limit: 1,
      }).then((res) => res.nTot);

    Promise.all(SERVICE_TABS.map(fetchCount))
      .then(([all, rpm, ccm, rpmCcm]) => {
        if (!cancelled) {
          setTabCounts({ all, rpm, ccm, rpmCcm });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTabCounts({ all: 0, rpm: 0, ccm: 0, rpmCcm: 0 });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userCustomData, listFilter, showParam, showServiceTabs]);

  useEffect(() => {
    if (!userCustomData) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setMembers([]);
    actions.reset();

    const query = buildMemberListQuery(
      listFilter,
      showParam,
      memberServiceTab,
      showServiceTabs,
    );

    actions
      .loadMembers(query, sortOption, pageNum)
      .then(() => {
        if (!cancelled) {
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCustomData,
    listFilter,
    sortParam,
    showParam,
    pageNum,
    memberServiceTab,
    showServiceTabs,
  ]);

  useEffect(() => {
    if (!memberAll?.length) {
      setMembers([]);
      setColumns(EMPTY_COLUMNS);
      return;
    }

    setMembers(memberAll);
    setColumns(buildColumns(memberAll));
  }, [memberAll]);

  if (loading) {
    return (
      <div className="messages box">
        <Dimmer active inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>
      </div>
    );
  }

  const allMembersSelected = _.isEqual(
    membersSelected,
    new Set(members.map((m) => m._id.toString())),
  );

  return (
    <ErrorBoundary>
      {showServiceTabs && (
        <Menu pointing secondary style={{ marginBottom: '1em' }}>
          <Menu.Item
            name="all"
            active={memberServiceTab === 'all'}
            onClick={() => handleServiceTabChange('all')}
          >
            {formatTabLabel('All', tabCounts.all)}
          </Menu.Item>
          <Menu.Item
            name="rpm"
            active={memberServiceTab === 'rpm'}
            onClick={() => handleServiceTabChange('rpm')}
          >
            {formatTabLabel('RPM', tabCounts.rpm)}
          </Menu.Item>
          <Menu.Item
            name="ccm"
            active={memberServiceTab === 'ccm'}
            onClick={() => handleServiceTabChange('ccm')}
          >
            {formatTabLabel('CCM', tabCounts.ccm)}
          </Menu.Item>
          <Menu.Item
            name="rpm-ccm"
            active={memberServiceTab === 'rpm-ccm'}
            onClick={() => handleServiceTabChange('rpm-ccm')}
          >
            {formatTabLabel('RPM + CCM', tabCounts.rpmCcm)}
          </Menu.Item>
        </Menu>
      )}
      <Grid>
        <Grid.Row columns={2}>
          <Grid.Column>
            <GroupActions
              membersFiltered={members}
              membersSelected={membersSelected}
              totalEnrolledCount={nTot}
              totalMembersCount={nTot}
              memberStartIdx={Math.min(pageNum * nLimit + 1, nTot)}
              memberEndIdx={Math.min(nTot, nLimit * (pageNum + 1))}
            />
          </Grid.Column>
          <Grid.Column textAlign="right">
            <Pagination
              size="mini"
              boundaryRange={0}
              defaultActivePage={pageNum + 1}
              siblingRange={1}
              totalPages={Math.ceil(nTot / nLimit)}
              onPageChange={(event, data) => {
                setPageNum(data.activePage - 1);
              }}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Table size="small" selectable basic="very">
        <TableHeader
          sortParam={sortParam}
          setSortParam={setSortParam}
          columns={columns}
          isCheckboxChecked={allMembersSelected}
          checkCheckbox={() => {
            if (allMembersSelected) {
              setMembersSelected(new Set());
            } else {
              setMembersSelected(
                new Set(members.map((m) => m._id.toString())),
              );
            }
          }}
        />
        <Table.Body>
          {members.map((member) => (
            <TableRow
              key={member._id}
              member={member}
              columns={columns}
              userTimeZone={userCustomData.timeZone}
              isMemberSelected={membersSelected.has(
                member._id.toString(),
              )}
              toggleMemberSelected={() => {
                const next = new Set(membersSelected);
                const id = member._id.toString();
                if (next.has(id)) {
                  next.delete(id);
                } else {
                  next.add(id);
                }
                setMembersSelected(next);
              }}
            />
          ))}
        </Table.Body>
      </Table>
      <Grid columns={1}>
        <Grid.Row>
          <Grid.Column textAlign="right">
            <Pagination
              size="mini"
              boundaryRange={0}
              defaultActivePage={pageNum + 1}
              siblingRange={1}
              totalPages={Math.ceil(nTot / nLimit)}
              onPageChange={(event, data) => {
                setPageNum(data.activePage - 1);
              }}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </ErrorBoundary>
  );
}
